import React, { useState } from 'react';
import { bookingShape, userShape } from 'constants/propTypes';
import { useHistory } from 'react-router';
import { useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import Checkbox from 'components/ui/Checkbox';

import { ContinueButton, CancelButton } from 'components/ui/Button';
import { ACTION_CODES } from 'constants/errors';
import AgentPortalError from 'errors/AgentPortalError';
import usePayIfLoyaltyAndConfirmBookingMutation from 'components/mutations/usePayIfLoyaltyAndConfirmBookingMutation';
import { CANCEL_BOOKING, ADD_NOTE_TO_BOOKING } from 'store/mutations';
import LoyaltyLoggedOutModal from 'components/shared/LoyaltyLoggedOutModal';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { userCanOfferQuotes } from 'utils/user';
import { bookingStatus } from 'types/booking';
import { isEurostarOffer } from 'utils/isEurostarOffer';

import { StyledButtonRow, StyledFormattedQuoteMessage } from './BookingPathReviewBookingCTA.styles';
import { isNewAriaBooking } from '../../../utils/booking';
import ReactTooltip from 'react-tooltip';
import { TooltipContainer } from '../../VoucherDetails/VoucherDetails.styles';
import { isSPXUser } from 'utils/user';

const BookingPathReviewBookingCTA = ({ user, bookingData }) => {
  const { totalPriceToBePaid, reference, productFamilies, isGroupBooking, status } = bookingData;
  const history = useHistory();
  const [hasLoyaltyAccountError, setLoyaltyAccountError] = useState(false);
  const [hasConfirmedFareConditions, setHasConfirmedFareConditions] = useState(false);
  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const membershipNumberUrl = membershipNumber ? `?membershipNumber=${membershipNumber}` : '';
  const isSubscription = new URLSearchParams(history.location.search).get('isSubscription');
  const isSubscriptionUrl = isSubscription ? `&isSubscription=${isSubscription}` : '';
  const subscriptionID = new URLSearchParams(history.location.search).get('subscriptionID');
  const subscriptionCode = new URLSearchParams(history.location.search).get('subscriptionCode');
  const subscriptionIDUrl = subscriptionID ? `&subscriptionID=${subscriptionID}` : '';
  const subscriptionCodeUrl = subscriptionCode ? `&subscriptionCode=${subscriptionCode}` : '';
  const nothingToBePaid = totalPriceToBePaid === 0;
  const shouldOfferQuoteWithoutHoldingSeats =
    isGroupBooking &&
    isEurostarOffer([...(bookingData?.outbound ?? []), ...(bookingData?.inbound ?? [])]) &&
    userCanOfferQuotes(user) &&
    status === bookingStatus.PROVISIONAL;
  const isLoyaltyLoggedOut =
    (productFamilies?.map(({ familyGroup }) => familyGroup).includes(productFamilyCodes.EUROSTAR_LOYALTY) ||
      productFamilies?.map(({ familyGroup }) => familyGroup).includes(productFamilyCodes.THPASS)) &&
    !membershipNumber;

  const isAriaBooking = isNewAriaBooking(bookingData);
  const isStationSPXUser = isSPXUser({ user });

  const routeAfterContinue = (shouldGoManageBooking) => {
    if (shouldGoManageBooking) {
      return `/manage/bookings/${reference}${membershipNumberUrl}${subscriptionIDUrl}${subscriptionCodeUrl}`;
    }

    return `/booking/${reference}/payment${membershipNumberUrl}${isSubscriptionUrl}${subscriptionIDUrl}${subscriptionCodeUrl}`;
  };

  const [confirmBooking, { error: confirmBookingError, loading: confirmBookingLoading }] = usePayIfLoyaltyAndConfirmBookingMutation();

  const [cancelBooking, { error: cancelBookingError, loading: cancelBookingLoading }] = useMutation(CANCEL_BOOKING);
  const [addNote, { loading: addNoteLoading }] = useMutation(ADD_NOTE_TO_BOOKING);

  const handleContinue = async () => {
    if (isLoyaltyLoggedOut) {
      setLoyaltyAccountError(true);

      return null;
    }

    const pathname = routeAfterContinue(nothingToBePaid);

    if (nothingToBePaid) {
      await confirmBooking({
        variables: { bookingData, membershipNumber, sendConfirmationEmail: !bookingData.isGroupBooking, actionBy: user.username }
      });
    }

    if (isStationSPXUser && hasConfirmedFareConditions) {
      await addNote({
        variables: {
          note: 'Journey schedule & conditions confirmed to customer',
          bookingReference: reference
        }
      });
    }

    return history.push(pathname, history.location.state);
  };

  const handleCancel = async () => {
    await cancelBooking({
      variables: { bookingReference: reference }
    });

    return isSubscription ? history.push(`/clubeurostar/${membershipNumber}`) : history.push(`/search${membershipNumberUrl}`);
  };

  if (confirmBookingError) {
    throw new AgentPortalError(confirmBookingError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      // eslint-disable-next-line max-len
      errorCTARedirect: `/booking/${reference}/review-booking${membershipNumberUrl}${isSubscriptionUrl}${subscriptionIDUrl}${subscriptionCodeUrl}`
    });
  }

  if (cancelBookingError) {
    throw new AgentPortalError(cancelBookingError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      // eslint-disable-next-line max-len
      errorCTARedirect: `/booking/${reference}/review-booking${membershipNumberUrl}${isSubscriptionUrl}${subscriptionIDUrl}${subscriptionCodeUrl}`
    });
  }

  const handleEmailQuote = () =>
    history.push(
      `/review-quote-confirmation/${reference}${membershipNumberUrl}${subscriptionIDUrl}${subscriptionCodeUrl}`,
      history.location.state
    );

  const shouldDisableButtons = confirmBookingLoading || cancelBookingLoading || addNoteLoading;

  const handleCloseModal = () => setLoyaltyAccountError(!hasLoyaltyAccountError);

  return (
    <>
      <LoyaltyLoggedOutModal isOpen={hasLoyaltyAccountError} onClose={handleCloseModal} />
      {isStationSPXUser && (
        <Checkbox
          checked={hasConfirmedFareConditions}
          onChange={(checked) => setHasConfirmedFareConditions(checked)}
          name="confirmed-fare-conditions"
          dataCy="confirmed-fare-conditions"
          label={<FormattedMessage id="reviewBooking.stngbspxFareConditionConfirmed" />}
        />
      )}
      <StyledButtonRow data-tip data-for="aria-tooltip">
        <ContinueButton
          data-testid="review-booking-button"
          data-cy="review-booking-button"
          type="submit"
          onClick={handleContinue}
          loading={confirmBookingLoading || addNoteLoading}
          disabled={shouldDisableButtons || isAriaBooking || (isStationSPXUser && !hasConfirmedFareConditions)}
          variant="primary"
        >
          <FormattedMessage id={nothingToBePaid ? 'bookingDetails.confirm' : 'refunds.takePayment'} />
        </ContinueButton>

        {isAriaBooking && (
          <ReactTooltip className="tooltip" id="aria-tooltip" data-cy="confirm-booking-tooltip" place="bottom">
            <TooltipContainer>
              <FormattedMessage id="confirm.onlyOnAria" />
            </TooltipContainer>
          </ReactTooltip>
        )}

        <CancelButton
          data-cy="booking-path-new-search"
          onClick={handleCancel}
          loading={cancelBookingLoading}
          disabled={shouldDisableButtons}
        >
          <FormattedMessage id="payments.cancel" />
        </CancelButton>

        {shouldOfferQuoteWithoutHoldingSeats && (
          <>
            <StyledFormattedQuoteMessage>
              <FormattedMessage id="reviewQuote.or" />
            </StyledFormattedQuoteMessage>
            <ContinueButton
              data-testid="booking-path-review-email-quote-no-holding"
              data-cy="booking-path-review-email-quote-no-holding"
              onClick={handleEmailQuote}
              buttonType="redirect"
              variant="secondary"
            >
              <FormattedMessage id="reviewQuote.emailQuoteWithoutHoldingSeats" />
            </ContinueButton>
          </>
        )}
      </StyledButtonRow>
    </>
  );
};

BookingPathReviewBookingCTA.propTypes = {
  user: userShape.isRequired,
  bookingData: bookingShape.isRequired
};

export default BookingPathReviewBookingCTA;
